import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import axiosInstance from '../services/axiosInstance';

const SHEETS = [
  { label: 'Weekly Egg Sales', desc: 'Crates sold, revenue, avg price, paid/outstanding by week' },
  {
    label: 'Weekly Egg Production',
    desc: 'Total eggs, broken, saleable crates, avg lay % by week',
  },
  { label: 'Weekly Expenditure', desc: 'Spend by category and week' },
  { label: 'Weekly Feed Production', desc: 'kg, bags, cost per feed type/brand by week' },
];

export default function AnalyticsExport() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await axiosInstance.get('/export/weekly-analytics', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const suffix =
        dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `narnia-farms-weekly-analytics-${suffix}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 680 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Analytics Export
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Download weekly aggregated data as an Excel workbook for analysis.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>
            Included Sheets
          </Typography>
          <List disablePadding dense>
            {SHEETS.map((s) => (
              <ListItem key={s.label} disablePadding sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <TableChartIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {s.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {s.desc}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <TextField
          label="From"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth
        />
        <TextField
          label="To"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: dateFrom || undefined }}
          size="small"
          fullWidth
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
        onClick={handleExport}
        disabled={loading}
        size="large"
      >
        {loading ? 'Generating…' : 'Download Excel'}
      </Button>

      {!dateFrom && !dateTo && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          No date range selected — all available data will be exported.
        </Typography>
      )}
    </Box>
  );
}
